/* eslint-disable no-console */
'use client';
import React from 'react';
import { resourceStringsRegistry, ResourceStringsKey } from './resourceStrings';
import { ConfigSettingsKey, configSettingsRegistry } from './configSettings';
import { globalContentsRegistry } from './globalContents';
export function CreateInitialResourceStringsObject() {
    const obj = {};
    Object.values(ResourceStringsKey).forEach((v) => {
        obj[v] = '';
    });
    return obj;
}
export function CreateInitialConfigSettingsObject() {
    const obj = {};
    Object.values(ConfigSettingsKey).forEach((v) => {
        obj[v] = '';
    });
    return obj;
}
export const GlobalContext = React.createContext({
    resourceStrings: CreateInitialResourceStringsObject(),
    configSettings: CreateInitialConfigSettingsObject(),
    globalContents: {},
    currentContext: {},
    deviceInfo: {},
});
export function GlobalProvider(props) {
    const { resourceStrings, configSettings, children, globalContents, currentContext, deviceInfo } = props;
    // This comnponent should only ever render once on page load so no need to memoize this value
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    let context = {
        resourceStrings: CreateInitialResourceStringsObject(),
        configSettings: CreateInitialConfigSettingsObject(),
        globalContents: {},
        currentContext,
        deviceInfo: {},
    };
    // Fetch data from local storage if available
    // Then overwrite from props (eg resourceStrings.length > 0) where available
    // Update local storage with new values
    if (typeof window !== 'undefined') {
        const storedValue = localStorage.getItem('context');
        if (storedValue) {
            try {
                const parsed = JSON.parse(storedValue);
                context = Object.assign(Object.assign({}, context), parsed);
            }
            catch (_a) {
                console.warn('Failed to parse stored context, ignoring.');
            }
        }
    }
    if ((resourceStrings === null || resourceStrings === void 0 ? void 0 : resourceStrings.length) > 0) {
        const resourceStringLookup = {};
        resourceStrings.forEach((resourceString) => {
            resourceStringLookup[resourceString === null || resourceString === void 0 ? void 0 : resourceString.key] = (resourceString === null || resourceString === void 0 ? void 0 : resourceString.value) || '';
        });
        resourceStringsRegistry.forEach((resourceStringKey) => {
            const matched = resourceStringLookup[resourceStringKey.toString()];
            if (matched !== undefined) {
                context.resourceStrings[resourceStringKey] = matched;
            }
            else {
                console.warn(`Resource string '${resourceStringKey}' not found in props.`);
                context.resourceStrings[resourceStringKey] = resourceStringKey;
            }
        });
    }
    else {
        console.warn('Resource strings empty in props, using local fallback.');
    }
    if ((configSettings === null || configSettings === void 0 ? void 0 : configSettings.length) > 0) {
        const configSettingLookup = {};
        configSettings.forEach((configSetting) => {
            configSettingLookup[configSetting === null || configSetting === void 0 ? void 0 : configSetting.key] = (configSetting === null || configSetting === void 0 ? void 0 : configSetting.value) || '';
        });
        configSettingsRegistry.forEach((configSettingsKey) => {
            const matched = configSettingLookup[configSettingsKey.toString()];
            if (matched !== undefined) {
                context.configSettings[configSettingsKey] = matched;
            }
            else {
                console.warn(`Config setting '${configSettingsKey}' not found in props.`);
            }
        });
    }
    else {
        console.warn('Config settings empty in props, using local fallback.');
    }
    if ((globalContents === null || globalContents === void 0 ? void 0 : globalContents.length) > 0) {
        const globalContentLookup = {};
        globalContents.forEach((globalContent) => {
            globalContentLookup[globalContent === null || globalContent === void 0 ? void 0 : globalContent.key] = globalContent;
        });
        globalContentsRegistry.forEach((globalContentKey) => {
            const matchedContent = globalContentLookup[globalContentKey];
            if (matchedContent) {
                context.globalContents[globalContentKey] = matchedContent;
            }
            else {
                console.warn(`Global content '${globalContentKey}' not found in props.`);
            }
        });
    }
    else {
        console.warn('Global contents empty in props, using local fallback.');
    }
    if (deviceInfo) {
        context.deviceInfo = deviceInfo;
    }
    if (typeof window !== 'undefined') {
        try {
            localStorage.setItem('context', JSON.stringify(context));
        }
        catch (_b) {
            console.warn('Failed to write context to localStorage');
        }
    }
    return React.createElement(GlobalContext.Provider, { value: context }, children);
}
export function useGlobalContext() {
    return React.useContext(GlobalContext);
}
